// FILE: AgentsSection.logic.ts
// Purpose: Pure helpers behind the Agents settings panel — sorting, name
//          validation, provider-driven filtering of model/tools, and
//          request mapping — kept apart from the component so they can be
//          unit tested without mounting React. Mirrors TeamsSection.logic.ts.
// Exports: `sortAgentsByName`, `validateAgentName`, `resolveNamesById`,
//          `filterToolsForProvider`, `resolveModelForProviderChange`,
//          `buildCreateRequest`, `buildUpdateRequest`, `EMPTY_AGENT_FORM`,
//          `agentToFormState`, `filterSubagentCandidates`,
//          `warnsAboutCodexShellForQa`, `CODEX_QA_SHELL_WARNING`.

import {
  AGENT_NAME_MAX_LENGTH,
  type ProviderModelOptions,
  DEFAULT_MODEL_BY_PROVIDER,
  expandManagedAgentToolIds,
  getManagedAgentToolOptions,
  MODEL_OPTIONS_BY_PROVIDER,
  type AdminAgentCreateRequest,
  type AdminAgentUpdateRequest,
  type ManagedAgent,
  type ManagedAgentId,
  type ManagedAgentMode,
  type ManagedAgentToolOption,
  type ProviderKind,
  type TagId,
  type TeamId,
} from "@taskin-code/contracts";
import { canBeSubagent, canRunSideChat } from "@taskin-code/shared/managedAgentMode";
import { describeAgentEffectiveTraits } from "@taskin-code/shared/managedAgentModelOptions";
import { getDefaultEffort, getModelCapabilities, hasEffortLevel } from "@taskin-code/shared/model";

/** Alphabetical by name, case-insensitive — matches `sortTeamsByName`. */
export function sortAgentsByName(agents: ReadonlyArray<ManagedAgent>): ReadonlyArray<ManagedAgent> {
  return agents.toSorted((left, right) =>
    left.name.localeCompare(right.name, undefined, { sensitivity: "base" }),
  );
}

export interface AgentNameValidation {
  readonly ok: boolean;
  readonly error?: string;
}

/** Same rule the server enforces (non-empty, <= AGENT_NAME_MAX_LENGTH) so the dialog can fail fast before the round trip. */
export function validateAgentName(name: string): AgentNameValidation {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: "Agent name is required." };
  }
  if (trimmed.length > AGENT_NAME_MAX_LENGTH) {
    return { ok: false, error: `Agent name must be ${AGENT_NAME_MAX_LENGTH} characters or fewer.` };
  }
  return { ok: true };
}

export interface NameLookup {
  readonly id: string;
  readonly name: string;
}

/**
 * Ids -> display names, in the given order. Generic over teams and
 * subagents: both are "an id list this agent points at, resolved against a
 * roster that might not have loaded yet or might no longer contain the id".
 * A missing id still needs a row — dropping it would hide a grant nobody
 * could then find to remove.
 */
export function resolveNamesById(
  ids: ReadonlyArray<string>,
  roster: ReadonlyArray<NameLookup>,
  fallback = "Unknown",
): ReadonlyArray<string> {
  const byId = new Map(roster.map((entry) => [entry.id, entry.name]));
  return ids.map((id) => byId.get(id) ?? fallback);
}

/**
 * Drop tool ids that are not offered to `provider`. Used when the editor's
 * provider select changes, so a Claude-only tool cannot survive a switch to
 * Codex and vice versa.
 */
export function filterToolsForProvider(
  toolIds: ReadonlyArray<string>,
  provider: ProviderKind,
): ReadonlyArray<string> {
  const valid = new Set(getManagedAgentToolOptions(provider).map((option) => option.id));
  return toolIds.filter((id) => valid.has(id));
}

export interface ManagedAgentToolSection {
  readonly section: string;
  readonly sectionLabel: string;
  readonly options: ReadonlyArray<ManagedAgentToolOption>;
}

/**
 * Groups the editor's flat tool option list into the sections the dialog
 * renders as its 2-column grid — one section per integration (Plane, Git,
 * GitHub, …) plus one per provider's builtins. Order follows first
 * appearance in `options`, which is already grouped server-by-server in the
 * shared catalog, so this needs no separate sort.
 */
export function groupToolOptionsBySection(
  options: ReadonlyArray<ManagedAgentToolOption>,
): ReadonlyArray<ManagedAgentToolSection> {
  const bySection = new Map<string, { sectionLabel: string; options: ManagedAgentToolOption[] }>();
  for (const option of options) {
    const existing = bySection.get(option.section);
    if (existing) {
      existing.options.push(option);
    } else {
      bySection.set(option.section, { sectionLabel: option.sectionLabel, options: [option] });
    }
  }
  return [...bySection.entries()].map(([section, { sectionLabel, options: sectionOptions }]) => ({
    section,
    sectionLabel,
    options: sectionOptions,
  }));
}

/**
 * When the provider changes, keep the current model only if it is still one
 * of the new provider's known slugs; otherwise fall back to that provider's
 * default. Free-form custom slugs are not preserved across a provider switch
 * on purpose — a Codex slug typed by hand is never valid for Claude, and
 * silently carrying it over would produce an agent that fails at dispatch
 * time instead of at save time.
 */
export function resolveModelForProviderChange(
  currentModel: string,
  provider: ProviderKind,
): string {
  const knownSlugs = MODEL_OPTIONS_BY_PROVIDER[provider].map((model) => model.slug) as string[];
  if (knownSlugs.includes(currentModel)) return currentModel;
  return DEFAULT_MODEL_BY_PROVIDER[provider];
}

export interface AgentFormState {
  readonly name: string;
  readonly description: string;
  readonly provider: ProviderKind;
  readonly model: string;
  readonly instructions: string;
  readonly tools: ReadonlyArray<string>;
  readonly teamIds: ReadonlyArray<TeamId>;
  readonly subagentIds: ReadonlyArray<ManagedAgentId>;
  readonly mode: ManagedAgentMode;
  readonly sideChatEnabled: boolean;
  readonly tagIds: ReadonlyArray<TagId>;
  readonly memoryStrict: boolean;
  /**
   * The effort this agent imposes on every turn, or `""` for "whatever the
   * model defaults to". Held flat rather than as a `ProviderModelOptions` block
   * because the form only ever edits the current provider's side, and
   * `buildModelOptions` reassembles the keyed shape on submit.
   */
  readonly effort: string;
  readonly fastMode: boolean;
  /** `true` unless the model has a thinking toggle and the admin turned it off. */
  readonly thinking: boolean;
}

export const EMPTY_AGENT_FORM: AgentFormState = {
  name: "",
  description: "",
  provider: "claudeAgent",
  model: DEFAULT_MODEL_BY_PROVIDER.claudeAgent,
  instructions: "",
  tools: [],
  teamIds: [],
  subagentIds: [],
  // A new agent starts at the widest mode, matching the column default: an admin
  // narrows it deliberately, and nothing about creating an agent implies which
  // way it is meant to be used.
  mode: "general",
  sideChatEnabled: false,
  tagIds: [],
  // Off by default, matching the column: a new agent behaves like every agent
  // did before tags existed until an admin narrows it deliberately.
  memoryStrict: false,
  // Empty is "the model's default", which is what every agent ran at before this
  // field existed.
  effort: "",
  fastMode: false,
  thinking: true,
};

/**
 * Seed the editor form from an existing agent.
 *
 * `tools` is expanded here: an agent saved before per-tool selection existed
 * stores a bare whole-server id (e.g. `"taskin_plane"`), which meant "every
 * tool of this server" at the time. Expanding on load pre-checks every one of
 * that server's boxes instead of leaving them all unchecked, and — since the
 * dialog always submits its full form state — the next save persists the
 * expanded granular ids, so the legacy form never has to be handled again
 * once an agent has been opened and saved once.
 */
export function agentToFormState(agent: ManagedAgent): AgentFormState {
  return {
    name: agent.name,
    description: agent.description ?? "",
    provider: agent.provider,
    model: agent.model,
    instructions: agent.instructions,
    tools: expandManagedAgentToolIds(agent.tools),
    teamIds: agent.teamIds,
    subagentIds: agent.subagentIds,
    mode: agent.mode,
    sideChatEnabled: agent.sideChatEnabled,
    tagIds: agent.tagIds,
    memoryStrict: agent.memoryStrict,
    effort: readPinnedEffort(agent.provider, agent.modelOptions),
    fastMode: pinnedFor(agent.provider, agent.modelOptions)?.fastMode === true,
    thinking: agent.modelOptions?.claudeAgent?.thinking !== false,
  };
}

/** The saved block for the provider the agent actually runs. */
const pinnedFor = (provider: ProviderKind, modelOptions: ProviderModelOptions | null | undefined) =>
  provider === "codex" ? modelOptions?.codex : modelOptions?.claudeAgent;

const readPinnedEffort = (
  provider: ProviderKind,
  modelOptions: ProviderModelOptions | null | undefined,
): string =>
  (provider === "codex"
    ? modelOptions?.codex?.reasoningEffort
    : modelOptions?.claudeAgent?.effort) ?? "";

/**
 * The effort ladder to offer for a form's current provider and model, plus which
 * entry is the model's own default.
 *
 * Empty for a slug outside the catalogue — the form lets an admin type one, and
 * there is nothing truthful to list. The UI hides the control in that case
 * rather than offering the previous model's ladder.
 */
export function agentEffortChoices(form: AgentFormState) {
  const caps = getModelCapabilities(form.provider, form.model);
  return {
    levels: caps.reasoningEffortLevels,
    defaultEffort: getDefaultEffort(caps),
    supportsFastMode: caps.supportsFastMode,
    supportsThinkingToggle: caps.supportsThinkingToggle,
  };
}

/**
 * Drop trait settings the new provider or model cannot honour.
 *
 * Same reasoning as `filterToolsForProvider`, which this mirrors: carrying a
 * Codex `ultra` onto a Claude model produces a save the server rejects, and
 * fixing it at save time is a worse place to find out than at the moment of the
 * change.
 */
export function resolveTraitsForModelChange(
  form: AgentFormState,
  next: { readonly provider: ProviderKind; readonly model: string },
): Pick<AgentFormState, "effort" | "fastMode" | "thinking"> {
  const caps = getModelCapabilities(next.provider, next.model);
  // An unknown slug has no ladder to check against, so nothing is dropped — the
  // server validates nothing for it either.
  const known = caps.reasoningEffortLevels.length > 0 || caps.supportsThinkingToggle;
  return {
    effort: !known || !form.effort || hasEffortLevel(caps, form.effort) ? form.effort : "",
    fastMode: form.fastMode && (!known || caps.supportsFastMode),
    thinking: form.thinking || (known && !caps.supportsThinkingToggle) ? true : form.thinking,
  };
}

/**
 * Reassemble the form's flat trait fields into the provider-keyed block the
 * contract stores. `null` when the agent imposes nothing beyond its model's
 * defaults, so a row that pins nothing stores `NULL` rather than `{}`.
 */
export function buildModelOptions(form: AgentFormState): ProviderModelOptions | null {
  const effort = form.effort.trim();
  if (form.provider === "codex") {
    const codex = {
      ...(effort ? { reasoningEffort: effort as never } : {}),
      ...(form.fastMode ? { fastMode: true } : {}),
    };
    return Object.keys(codex).length > 0 ? { codex } : null;
  }
  const claudeAgent = {
    ...(form.thinking ? {} : { thinking: false }),
    ...(effort ? { effort: effort as never } : {}),
    ...(form.fastMode ? { fastMode: true } : {}),
  };
  return Object.keys(claudeAgent).length > 0 ? { claudeAgent } : null;
}

const trimmedDescription = (description: string): string | null => {
  const trimmed = description.trim();
  return trimmed.length === 0 ? null : trimmed;
};

/** Map the editor's form state to the create request body. */
export function buildCreateRequest(form: AgentFormState): AdminAgentCreateRequest {
  return {
    name: form.name.trim(),
    description: trimmedDescription(form.description),
    provider: form.provider,
    model: form.model.trim(),
    instructions: form.instructions,
    tools: form.tools,
    teamIds: form.teamIds,
    subagentIds: form.subagentIds,
    mode: form.mode,
    // Kept consistent with the mode the form is submitting rather than sent as
    // the checkbox left it: the server refuses the pair, and refusing a save the
    // UI could have made sensible is a worse experience than the checkbox
    // visibly not applying to a subagent.
    sideChatEnabled: canRunSideChat(form.mode) && form.sideChatEnabled,
    tagIds: form.tagIds,
    memoryStrict: form.memoryStrict,
    modelOptions: buildModelOptions(form),
  };
}

/**
 * Map the editor's form state to the update patch body. The editor always
 * shows — and therefore always submits — the agent's complete state, so
 * every field is sent every time; per `ManagedAgentPatch`'s contract that is
 * equivalent to a full save, not a partial one.
 */
export function buildUpdateRequest(form: AgentFormState): AdminAgentUpdateRequest {
  return buildCreateRequest(form);
}

/**
 * A one-line summary of the traits an agent imposes, for the roster table.
 *
 * Built from `describeAgentEffectiveTraits` so it reports what a turn will
 * *actually* run at, not only what was typed into the form: an agent that pins
 * nothing still shows its model's default, because "nothing" is not what the
 * reader wants to know.
 */
export function describeAgentTraits(agent: ManagedAgent): string {
  const traits = describeAgentEffectiveTraits(agent);
  const parts: string[] = [];
  if (traits.effort) parts.push(`Effort ${traits.effort}`);
  if (traits.fastMode) parts.push("Fast");
  if (traits.thinking === false) parts.push("Thinking off");
  return parts.length === 0 ? "—" : parts.join(" · ");
}

/**
 * The agents this editor may offer as delegation targets.
 *
 * Two exclusions, for two different reasons. The agent being edited is dropped
 * because an agent cannot delegate to itself — the server refuses the cycle
 * anyway. A `primary` agent is dropped because its mode says it is never
 * anyone's subagent, and offering the checkbox would let the admin build a save
 * the server has to reject.
 *
 * An agent already wired up is kept regardless, so an edge that predates a mode
 * change stays visible and unpickable-by-accident rather than silently
 * disappearing from the list it is stored in: the admin can see it, uncheck it
 * and save, which is exactly what the server's `mode_conflict` asks them to do.
 */
export function filterSubagentCandidates(input: {
  readonly agents: ReadonlyArray<ManagedAgent>;
  readonly excludeAgentId: ManagedAgentId | null;
  readonly selectedSubagentIds: ReadonlyArray<ManagedAgentId>;
}): ReadonlyArray<ManagedAgent> {
  const selected = new Set<string>(input.selectedSubagentIds);
  return input.agents.filter(
    (agent) =>
      agent.id !== input.excludeAgentId && (canBeSubagent(agent.mode) || selected.has(agent.id)),
  );
}

/**
 * What a QA agent on Codex actually gets, said out loud in the editor.
 *
 * Codex exposes `shell` and `apply_patch` unconditionally: they are not tools
 * this product mounts, so clearing their checkboxes changes nothing (see the
 * `note` on those two options in the catalog). For most agents that is a
 * footnote. For a QA agent it is the whole point of the design — the QA agent
 * is supposed to reach an environment *only* through the `qa_*` tools, so that
 * every write to `.env` goes through `qa_env_config_set` and shows up as a
 * `config-changed` event with a thread behind it. On Codex it can also just
 * edit the file, and nothing records that it did.
 *
 * So the warning fires exactly where the mismatch is: Codex, plus at least one
 * QA tool selected. The permissions of the human still apply to everything that
 * goes through the server (git, previews, the tools themselves) — what is lost
 * is the guarantee that the environment's contents only change through them.
 */
export const CODEX_QA_SHELL_WARNING =
  "En Codex no se puede vetar `shell` ni `apply_patch`: este agente podrá editar el " +
  "entorno por fuera de las tools de QA, y esos cambios no quedan registrados como " +
  "modificaciones del entorno. El agente QA de referencia corre sobre Claude.";

export function warnsAboutCodexShellForQa(form: {
  readonly provider: ProviderKind;
  readonly tools: ReadonlyArray<string>;
}): boolean {
  if (form.provider !== "codex") return false;
  // Expanded first: a legacy whole-server id (`"taskin_qa"`) selects every QA
  // tool, and it is the selection that matters, not how it was written down.
  return expandManagedAgentToolIds(form.tools).some((toolId) => toolId.startsWith("taskin_qa:"));
}
